import { CloseIcon } from 'components/icons'
import { generateContext } from 'context/generate-context'
import classNames from 'helpers/class-names'
import { Dispatch, FC, PropsWithChildren, useEffect, useState } from 'react'
import { Portal, Tooltip } from 'ui'

type ModalContext = {
  visible: boolean
  setVisible: Dispatch<boolean>
  onClose: () => void
}

const [_ModalProvider, useModal] = generateContext<ModalContext>(null)

export type ModalProps = {
  open: boolean
  onClose: () => void
  allowBackdropClose?: boolean
  title?: string
  size?: 'small' | 'compact' | 'medium' | 'large' | 'full-screen'
  overflowScroll?: boolean
  className?: string
}

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  children,
  open,
  allowBackdropClose = true,
  onClose,
  size = 'medium',
  className = '',
  overflowScroll = true,
}) => {
  const [visible, setVisible] = useState<boolean>(open)
  const widthClass = {
    small: 'sm:w-92',
    compact: 'sm:w-120',
    medium: 'sm:w-144',
    large: 'sm:w-5/6',
    'full-screen': 'sm:w-full h-full',
  }[size]

  useEffect(() => {
    setVisible(open)
  }, [open])

  if (!visible) {
    return <></>
  }

  return (
    <Portal>
      <_ModalProvider
        value={{
          visible,
          setVisible,
          onClose,
        }}>
        <div
          data-testid="modal-backdrop"
          onClick={(e) => {
            e.stopPropagation()

            if (allowBackdropClose) {
              setVisible(false)
              onClose()
            }
          }}
          className={classNames(
            'fixed left-0 top-0 z-50 h-full w-full bg-gray-900 text-center',
            'hide-scrollbar overflow-scroll transition duration-200 ease-in-out',
            {
              'bg-opacity-70 backdrop-blur dark:bg-opacity-90': visible,
              'pointer-events-none bg-opacity-0 backdrop-blur-0 dark:bg-opacity-0':
                !visible,
            },
          )}
        />
        <div
          className={classNames(
            widthClass,
            'fixed inset-x-0 top-0 z-50 mx-auto p-4 sm:items-center sm:p-0',
          )}
          onClick={(e) => e.stopPropagation()}>
          <div
            role="alertdialog"
            aria-modal="true"
            className={classNames(
              widthClass,
              'relative w-full overflow-hidden bg-white shadow-xl dark:bg-gray-500',
              'text-left transition duration-100 sm:mx-auto sm:w-auto',
              {
                'mt-10 rounded-xl': size !== 'full-screen',
                'scale-100 opacity-100': visible,
                'scale-50 opacity-0': !visible,
              },
              className,
            )}>
            <div
              className={classNames({
                'h-full': size === 'full-screen',
                'max-h-[calc(100vh-6rem)]': size !== 'full-screen',
                'hide-scrollbar overflow-y-scroll ': overflowScroll,
              })}>
              {children}
            </div>
          </div>
        </div>
      </_ModalProvider>
    </Portal>
  )
}

type ModalTitleProps = {
  closeButton?: boolean
  className?: string
}
export const ModalTitle: FC<PropsWithChildren<ModalTitleProps>> = ({
  children,
  closeButton = true,
  className = '',
}) => {
  const { setVisible, onClose } = useModal()
  return (
    <div
      id="dialogTitle"
      aria-labelledby="dialogTitle"
      aria-describedby="dialogDesc"
      className={classNames(
        'sticky right-0 top-0 flex w-full items-start border-b border-gray-100 bg-white px-4 py-2 pr-2 text-lg text-gray-600 sm:px-6 sm:py-4 sm:pr-4 sm:text-xl',
        className,
      )}>
      <div className="w-full bg-white">{children}</div>
      {closeButton && (
        <Tooltip tip="Close">
          <button
            aria-label="Close"
            className="block cursor-pointer rounded-lg p-1 text-gray-300 outline-none
                    transition-all duration-300 hover:bg-gray-100/50 hover:text-gray-400
                    focus-visible:ring-2 focus-visible:ring-info/75 active:scale-90"
            onClick={() => {
              setVisible(false)
              onClose()
            }}>
            <CloseIcon className="size-5" />
          </button>
        </Tooltip>
      )}
    </div>
  )
}

type ModalBodyProps = {
  className?: string
}
export const ModalBody: FC<PropsWithChildren<ModalBodyProps>> = ({
  children,
  className,
}) => {
  return (
    <div className={classNames('hide-scrollbar w-full p-4 sm:p-6', className)}>
      {children}
    </div>
  )
}

type ModalFooterProps = {
  className?: string
}
export const ModalFooter: FC<PropsWithChildren<ModalFooterProps>> = ({
  children,
  className,
}) => {
  return (
    <div
      className={classNames(
        'flex w-full border-t border-gray-100 p-4 sm:p-6 sm:py-4',
        className,
      )}>
      {children}
    </div>
  )
}
